import React, { useEffect, useState } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import { FaChartPie, FaGraduationCap, FaUsers, FaCheckCircle, FaDownload } from 'react-icons/fa';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const InstructorReports = () => {
  const [data, setData]       = useState(null);
  const [courses, setCourses] = useState([]);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.instructor.getDashboard(),
      api.instructor.getCourses(),
      api.instructor.getUsers()
    ])
      .then(([dashRes, courseRes, userRes]) => {
        if (dashRes.data?.success)   setData(dashRes.data.data);
        if (courseRes.data?.success) setCourses(Array.isArray(courseRes.data.data) ? courseRes.data.data : []);
        if (userRes.data?.success)   setUsers(Array.isArray(userRes.data.data)   ? userRes.data.data   : []);
      })
      .catch((err) => toast.error('Failed to load report: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, []);

  const totalEnrollments = data?.enrollmentStats?.reduce((s, e) => s + e.total,     0) ?? 0;
  const totalCompleted   = data?.enrollmentStats?.reduce((s, e) => s + e.completed, 0) ?? 0;
  const completionRate   = totalEnrollments > 0 ? Math.round((totalCompleted / totalEnrollments) * 100) : 0;

  const handleExportCSV = () => {
    if (!data) return;
    const rows = [['Course ID', 'Course Name', 'Total Enrolled', 'Completed', 'Completion %']];
    courses.forEach((c) => {
      const stat = data.enrollmentStats?.find((s) => s.course_id === c.id) || {};
      const pct  = stat.total > 0 ? Math.round(((stat.completed || 0) / stat.total) * 100) : 0;
      rows.push([c.id, stat.course_title || c.name || c.title || '', stat.total || 0, stat.completed || 0, `${pct}%`]);
    });
    const csv  = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `instructor_report_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-3 sm:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h4 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-red-600 mb-1">
            Reports &amp; Analytics
          </h4>
          <p className="text-sm text-gray-500">Analytics for your assigned courses and learners.</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={loading || !data}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl shadow disabled:opacity-50 text-sm"
        >
          <FaDownload /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <Spinner animation="border" variant="primary" style={{ width: 48, height: 48 }} />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Assigned Courses',  value: data?.courseCount ?? 0,  icon: FaGraduationCap, grad: 'from-purple-500 to-purple-700'  },
              { label: 'Assigned Learners', value: data?.userCount ?? 0,    icon: FaUsers,         grad: 'from-blue-500 to-blue-700'      },
              { label: 'Total Enrollments', value: totalEnrollments,         icon: FaChartPie,      grad: 'from-emerald-500 to-teal-600'   },
              { label: 'Completion Rate',   value: `${completionRate}%`,     icon: FaCheckCircle,   grad: 'from-violet-500 to-purple-700'  }
            ].map(({ label, value, icon: Icon, grad }) => (
              <div key={label} className="bg-white rounded-2xl shadow border border-gray-100 p-4 flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center shadow-md flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xl font-extrabold text-gray-800">{value}</div>
                  <div className="text-xs text-gray-500">{label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Course Completion Table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-purple-500 to-red-600 px-5 py-3">
              <h6 className="text-white font-bold text-base mb-0">Course Completion Analytics</h6>
            </div>
            {courses.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No courses assigned yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Course</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Enrolled</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Completed</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600 min-w-[180px]">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((c) => {
                      const stat = data?.enrollmentStats?.find((s) => s.course_id === c.id) || {};
                      const pct  = stat.total > 0 ? Math.round(((stat.completed || 0) / stat.total) * 100) : 0;
                      return (
                        <tr key={c.id} className="border-b border-gray-50 hover:bg-purple-50/30">
                          <td className="px-5 py-3 font-medium text-gray-700">{stat.course_title || c.name || c.title || `#${c.id}`}</td>
                          <td className="px-5 py-3 text-gray-600">{stat.total || 0}</td>
                          <td className="px-5 py-3">
                            <Badge bg="success" className="px-2 py-1">{stat.completed || 0}</Badge>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-2">
                                <div className="bg-gradient-to-r from-purple-500 to-red-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs font-medium text-gray-600 w-10 text-right">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Assigned Users */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-5 py-3">
              <h6 className="text-white font-bold text-base mb-0">Assigned Learners ({users.length})</h6>
            </div>
            {users.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No learners assigned yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Email</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-purple-50/20">
                        <td className="px-5 py-3 font-medium text-gray-700">{u.first_name} {u.last_name}</td>
                        <td className="px-5 py-3 text-gray-500">{u.email}</td>
                        <td className="px-5 py-3">
                          <Badge bg={u.status === 1 ? 'success' : 'secondary'} className="text-xs">
                            {u.status === 1 ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InstructorReports;
